// Rate-structure intelligence for Zero-to-Hero revenue shares. The offer's
// framing ("steps down as you repay") is only true when growth < payback —
// this classifier names what the numbers ACTUALLY do over the life of the
// partnership, in plain language, so a founder never discovers a perpetuity
// at signing. Isomorphic: the offer page re-classifies live as numbers are
// countered, the admin diff shows structure transitions, and the negotiation
// advisor reasons about them.

export type RateStructureKind =
  | 'steps-down'
  | 'flat-perpetuity'
  | 'steps-up'
  | 'ends-after-payback'

export type RateStructure = {
  kind: RateStructureKind
  /** Short heading suffix: "Revenue share — {headline}". */
  headline: string
  /** Plain-transparent sentence for the founder, perpetuity stated outright. */
  founderLine: string
  /** Whether any component (growth or scaling) runs for the partnership's life. */
  perpetual: boolean
  scalingPerpetual: boolean
  tone: 'emerald' | 'gold' | 'amber'
}

const pct = (n: number) => `${Math.round(n * 10) / 10}%`

export function classifyRateStructure(terms: {
  paybackRatePct?: number | null
  growthRatePct?: number | null
  scalingRatePct?: number | null
}): RateStructure {
  const payback = Number(terms.paybackRatePct) || 0
  const growth = Number(terms.growthRatePct) || 0
  const scaling = Number(terms.scalingRatePct) || 0
  const scalingPerpetual = scaling > 0
  const scalingNote = scalingPerpetual
    ? ` The ${pct(scaling)} scaling share runs alongside for the life of the partnership.`
    : ''

  if (growth <= 0) {
    return {
      kind: 'ends-after-payback',
      headline: 'ends after payback',
      founderLine: scalingPerpetual
        ? `Your ${pct(payback)} rate ends entirely once the build is repaid — only the ${pct(scaling)} scaling share continues afterward.`
        : `Your ${pct(payback)} rate ends entirely once the build is repaid. No ongoing revenue share after that.`,
      perpetual: scalingPerpetual,
      scalingPerpetual,
      tone: 'emerald',
    }
  }
  if (growth < payback) {
    return {
      kind: 'steps-down',
      headline: 'steps down as you repay',
      founderLine: `Your rate steps down from ${pct(payback)} to ${pct(growth)} once the build is repaid, and the ${pct(growth)} growth rate continues for the life of the partnership.${scalingNote}`,
      perpetual: true,
      scalingPerpetual,
      tone: 'emerald',
    }
  }
  if (growth === payback) {
    return {
      kind: 'flat-perpetuity',
      headline: 'one flat rate, ongoing',
      founderLine: `Flat ${pct(payback)} — your rate does not step down after payback; it continues at the same level for the life of the partnership.${scalingNote}`,
      perpetual: true,
      scalingPerpetual,
      tone: 'gold',
    }
  }
  return {
    kind: 'steps-up',
    headline: 'steps up after payback',
    founderLine: `Your rate increases from ${pct(payback)} to ${pct(growth)} once the build is repaid, and the higher ${pct(growth)} rate continues for the life of the partnership.${scalingNote}`,
    perpetual: true,
    scalingPerpetual,
    tone: 'amber',
  }
}
